import type { LocaleData } from '../../../models/locale/locale-data';
import type { LandingVariantCopy } from '../../../models/landing/variant';

// New-UX (`-v1`) shared copy — the approved redesign strings (mockup pack
// round 3, 26-Aug-2026 product feedback). The three `-v1` variant keys reuse
// this block; ` **` footnote markers on the CTA headings are appended by the
// components, not stored here.
const v2Copy: LandingVariantCopy = {
  banner: 'REGISTER NOW',
  heroCopy: 'Smart choice on your purchase!',
  heroSubCopy1: 'Now register your appliance within 60 days to unlock your free benefits',
  benefitTicks: [
    '10-year free spare parts warranty*',
    'Genuine Haier parts',
    'Optional extra protection for accidental damage and breakdowns',
  ],
  heroDisclaimer:
    'The warranty covers parts such as motors, pumps, control boards and other eligible components.',
  registration: 'Call now to activate',
  ctaPhoneText: 'Call 0800 085 5962',
  phoneSubCopy: 'Lines open Monday to Friday 8am-8pm, Saturday 8am-6pm and Sunday 9am-6pm.',
  onlineCopy: 'Register online in minutes',
  ctaOnlineText: 'Register online',
  sixtyDayHeader: 'Register within 60 days',
  sixtyDayBody: 'Activate your free 10-year parts warranty before your registration window closes',
};

// LP1 hybrid (`phone-only-v2`, Sept 2026) — LP COPY V2_SEPT 2026 sheets as
// applied in the approved mockups (claude-notes/lp-hybrid-lp1). Dials the
// same number as V1: `ctaPhoneLink` is V1's (undefined = the base link).
// The `**` on the first heading is appended by PhoneOnlyV2.
// The v2 pages drop "scan the QR code or " from the ** footnote (no QR code on the
// page; product, 28 Sep 2026). V1 and control keep the base text.
const hybridPhoneNumberAsterix = [
  '** Haier has appointed Domestic & General Insurance PLC to provide product registration services and insurance policies to accompany its products. At Domestic & General, we treat your data with care. You can relax knowing it’s completely secure and only ever used responsibly. To find out more about how we use your information, head to <0>https://www.domesticandgeneral.com/mydata/haier</0>',
];

const lp1HybridCopy: LandingVariantCopy = {
  banner: 'Register your appliance',
  offerHeadline: ['10 years of repair services', 'with free parts*'],
  sixtyDayHeader: 'Register within 60 days of purchase',
  registration: 'Call now to activate',
  ctaPhoneText: 'Call to register – 0800 085 5962',
  ctaPhoneLink: v2Copy.ctaPhoneLink,
  benefitsHeader: 'Activate your free guarantee today for:',
  benefitTicks: [
    'Free 10-year parts guarantee',
    'Genuine Haier parts',
    'Optional accidental-damage cover',
  ],
  phoneSubCopy: v2Copy.phoneSubCopy,
  repeatHeading: 'Ready to register?',
  phoneNumberAsterix: hybridPhoneNumberAsterix,
  ctaPhoneRepeatText: 'Call now – 0800 085 5962',
};

// LP2 hybrid (`phone-and-link-v2`, Sept 2026) — the LP1 hybrid copy plus the online
// block (product list, 28 Sep); no repeat button. Approved mockup:
// claude-notes/lp-hybrid-lp1/EN_LP2_hybrid_mockups.pdf.
const lp2HybridCopy: LandingVariantCopy = {
  banner: lp1HybridCopy.banner,
  offerHeadline: lp1HybridCopy.offerHeadline,
  sixtyDayHeader: lp1HybridCopy.sixtyDayHeader,
  registration: lp1HybridCopy.registration,
  ctaPhoneText: 'Call to register: 0800 085 5962',
  ctaPhoneLink: v2Copy.ctaPhoneLink,
  phoneSubCopy: lp1HybridCopy.phoneSubCopy,
  onlineCopy: 'Register online in minutes',
  ctaOnlineText: 'Register online',
  benefitsHeader: lp1HybridCopy.benefitsHeader,
  benefitTicks: lp1HybridCopy.benefitTicks,
  phoneNumberAsterix: hybridPhoneNumberAsterix,
};

export const data: LocaleData = {
  banner: 'Activate your FREE*',
  heroNumber: '10',
  heroCopy: 'year parts guarantee',
  heroSubCopy1: 'Your new appliance comes with a 2-year manufacturer\u2019s guarantee.',
  heroSubCopy2:
    'By registering your appliance, you will receive an additional 8-year parts guarantee \u2013 free* of charge.',
  registration: 'Call now to activate**',
  ctaPhoneText: '0800 085 5962',
  ctaPhoneLink: 'tel:08000855962',
  phoneSubCopy:
    "Activation by phone is quick and provides additional information about your manufacturer's and 10-year parts guarantee. Lines are open Monday to Friday 8am-8pm, Saturday 8am-6pm and Sunday 9am-6pm.",
  onlineCopy: 'Alternatively, you can register online.',
  ctaOnlineText: 'Click here.',
  ctaOnlineLink: 'https://haier.partners.domesticandgeneral.com/register/',
  onlineJourneyLaunch: 'Live',
  variants: {
    'phone-only-v1': v2Copy,
    'phone-and-link-v1': v2Copy,
    'phone-only-v2': lp1HybridCopy,
    'phone-and-link-v2': lp2HybridCopy,
    'link-only-v1': {
      ...v2Copy,
      ctaOnlineText: 'Click here to register now',
      linesClosedMessage:
        'Our phone lines are currently closed, but you can register online anytime',
    },
    // LP3 COPY V1_JULY 2026.xlsx (HAIER tab; "LP3 copy" master settles ambiguities).
    'link-only': {
      banner: 'Activate your FREE',
      heroCopy: 'year parts guarantee*',
      heroSubCopy1: 'Activation necessary',
      heroSubCopy2: 'within 60 days from appliance purchase',
      registration: 'Register your appliance now to unlock your free benefits**',
      onlineCopy: 'Our phone lines are currently closed, but you can register online anytime',
      ctaOnlineText: 'Register online',
    },
  },
  benefitsHeader: 'Activate your free guarantee today to:',
  benefits: [
    'Gain an additional 8-year parts guarantee',
    'Record your proof of purchase date',
    'Learn about important appliance and safety updates',
    'Get easy access to Haier-approved repair services',
  ],
  footerLinks: [
    {
      text: 'Website terms of use',
      link: 'https://www.domesticandgeneral.com/web-terms-conditions',
    },
    { text: 'Privacy notice', link: 'https://www.domesticandgeneral.com/mydata/haier' },
    { text: 'Cookie notice', link: 'https://www.domesticandgeneral.com/privacy-cookies-policy' },
  ],
  footerCopy:
    'Service plans and maintenance & support plans are provided by Domestic & General Services Limited. Company No. 1970780. Insurance policies are provided by Domestic & General Insurance PLC. Company No. 485850. Registered Office for both companies: Swan Court, 11 Worple Road, Wimbledon, London SW19 4JS. Both companies are registered in England and Wales. Domestic & General Insurance PLC is authorised by the Prudential Regulation Authority and regulated by the Financial Conduct Authority and the Prudential Regulation Authority. Financial register number 202111. Domestic & General Insurance PLC is an insurance undertaking, not an intermediary. We are the underwriter of the insurance and do not provide a personal recommendation or advice. Domestic & General Insurance PLC pay their sales agents periodic incentives (such as bonus payments) if they meet certain quality and sales targets. All prices include all applicable taxes (including IPT).',
  heroAsterix: [
    '* By registering your appliance, you will activate your FREE 10-year parts guarantee (inclusive of your legal guarantee period). If not activated, proof of purchase needed. Labour charges and other conditions apply. For T&C’s, visit <0>www.haier-europe.com/en_GB/pages/terms-conditions-extended-warranty</0>',
  ],
  heroAsterixLinks: ['www.haier-europe.com/en_GB/pages/terms-conditions-extended-warranty'],
  phoneNumberAsterix: [
    '** Haier has appointed Domestic & General Insurance PLC to provide product registration services and insurance policies to accompany its products. At Domestic & General, we treat your data with care. You can relax knowing it’s completely secure and only ever used responsibly. To find out more about how we use your information, scan the QR code or head to <0>https://www.domesticandgeneral.com/mydata/haier</0>',
  ],
  phoneNumberAsterixLinks: ['https://www.domesticandgeneral.com/mydata/haier'],
  additionalSmallPrint:
    'Haier is a trading name of Haier Smart Home UK & I Ltd. Registered in England and Wales. Company no. 02521528 with its registered office at 302 Bridgewater Place, Birchwood Park, Birchwood, Warrington WA3 6XG.',
  languageSwitcherLabel: 'Country',
  languageSwitcherPlaceholder: 'Select your country',
  languageList: [
    { label: 'Germany', value: 'de-DE' },
    { label: 'United Kingdom', value: 'en-EN' },
    { label: 'Spain', value: 'es-ES' },
    { label: 'France', value: 'fr-FR' },
    { label: 'Italy', value: 'it-IT' },
  ],
};
